import { z } from 'zod';
/**
 * AgentQuestion — one row in the "9 things I want to ask you" queue
 * (issue #166 PR A). The agent stockpiles questions it would otherwise
 * have to guess at; the CEO clears them on their own cadence. Each
 * question carries a topic, a Fraunces-h3 body, a muted-body "why I'm
 * asking", and up to five suggested-answer chips so the CEO can resolve
 * with one tap. `blocksBriefing` flags the question as a precondition
 * for tomorrow's brief — the agent will hold synthesis on the affected
 * topic until the CEO answers.
 *
 * Persisted at `organizations/{orgId}/agentQuestions/{id}`; surfaced by
 * `GET /api/orgs/:o/agent/questions` (open queue) and resolved by
 * `PATCH /api/orgs/:o/agent/questions/:id`.
 */
/** Priority bucket — drives the queue ordering + the optional badge. */
export const AgentQuestionPriority = z.enum(['high', 'med', 'low']);
export const AgentQuestion = z.object({
    id: z.string(),
    orgId: z.string(),
    /** UID the question is addressed to (today: the CEO; later: any leader). */
    ownerUid: z.string(),
    /** When the agent decided to ask. */
    ts: z.string().datetime(),
    priority: AgentQuestionPriority,
    /** Short topic tag — eyebrow-mono caption in the question card. */
    topic: z.string(),
    /** Fraunces h3 body — the question itself. */
    question: z.string(),
    /** Muted body explaining why the agent is asking now. */
    why: z.string(),
    /** Suggested-answer chips. Up to five. */
    suggested: z.array(z.string()).max(5),
    /** When true, the agent is holding tomorrow's brief until this resolves. */
    blocksBriefing: z.boolean(),
    /** When the CEO answered (undefined while open). */
    resolvedAt: z.string().datetime().optional(),
    /** The resolution copy — free-text or the chosen `suggested` entry. */
    resolvedAnswer: z.string().optional(),
});
